import type { CellContext, Row } from '@tanstack/react-table';
import { nanoid } from 'nanoid';
import * as React from 'react';
import type { Path } from 'react-hook-form';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import { filterMap } from '@acceligentllc/shared/utils/array';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { CompoundUnitEnum, QuantityUnitLabel } from '@acceligentllc/shared/enums/quantityUnit';

import { BillingCodeEditMode } from 'ab-enums/billingCodeEditMode.enum';

import { UNIQUE_ID_SIZE } from 'ab-common/constants/value';

import ColorSquare from 'af-components/ColorSquare';
import type { FooterButton } from 'af-components/Controls/SimpleTable/types';
import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';
import type { SimpleTableRow } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import SimpleTableRHFWrapper from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import { validateFieldArray, validateOneField } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper/validationRHF';
import type { TableRef } from 'af-components/RHFTableCellComponents/SimpleTable';
import DropdownCellRenderer from 'af-components/RHFTableCellRenderers/DropdownCellRenderer';
import InputCellRenderer from 'af-components/RHFTableCellRenderers/InputCellRenderer';
import TextAreaCellRenderer from 'af-components/RHFTableCellRenderers/TextAreaCellRenderer';
import Tooltip from 'af-components/Tooltip';

import type { NormalizedImport } from '..';
import BillingCodeArchiveModal from './BillingCodeArchiveModal';
import BillingCodeEditModeModal from './BillingCodeEditModeModal';
import styles from './styles.module.scss';
import type JobUpsertFM from '../../formModel';
import { type BillingCodeGroupFM } from '../../formModel';

export type BillingCodeFM = Required<JobUpsertFM>['billingCodes'][0];

// RHF-only stable identity so fieldArray doesn't hijack `id`
export type BillingCodeRHFRow = BillingCodeFM & { rhfId: string; };

interface DropdownOption<T> {
	id: T;
	label: string;
}

interface GroupDropdownOption extends DropdownOption<string> {
	color: Nullable<ColorPalette>;
}

export type CustomFormErrors<Model> = { [K in keyof Model]: Model[K] extends object ? CustomFormErrors<Model[K]> : string };
export type BillingCodeErrors = CustomFormErrors<BillingCodeRHFRow[]>;

const compoundKeys = Object.keys(CompoundUnitEnum);
const quantityUnitOptions: DropdownOption<QuantityUnitType>[] = filterMap(
	Object.keys(QuantityUnitLabel),
	(_unit: QuantityUnitType) => !compoundKeys.includes(_unit),
	(_unit: QuantityUnitType) => ({ id: _unit, label: QuantityUnitLabel[_unit] })
);

export type BillingCodeColumn = keyof BillingCodeRHFRow;
export type BillingCodeFieldPath = `billingCodes.${number}.${BillingCodeColumn}`;

const uniqueFields: BillingCodeColumn[] = ['lineItemNumber', 'customerId'];
const requiredFields: BillingCodeColumn[] = ['lineItemNumber', 'customerId', 'unit', 'unitPrice', 'description'];
const numberFields: BillingCodeColumn[] = ['lineItemNumber', 'customerNumber', 'ownerNumber', 'unitPrice', 'bidQuantity'];

function isBillingCodeColumn(value: string): value is BillingCodeColumn {
	return ([
		'rhfId',
		'id',
		'databaseId',
		'deletedAt',
		'editMode',
		'customerId',
		'customerNumber',
		'ownerId',
		'ownerNumber',
		'unit',
		'unitPrice',
		'bidQuantity',
		'groupId',
		'groupKey',
		'groupColor',
		'group',
		'lineItemNumber',
		'description',
		'createdAt',
	] as const).includes(value as BillingCodeColumn);
}

function parseBillingCodePath(name: string): Nullable<{ index: number; field: BillingCodeColumn; }> {
	// expecting: billingCodes.<index>.<field>
	const parts = name.split('.');
	if (parts.length !== 3) {
		return null;
	}
	if (parts[0] !== 'billingCodes') {
		return null;
	}

	const index = +parts[1];
	if (!Number.isInteger(index) || index < 0) {
		return null;
	}

	const fieldRaw = parts[2];
	if (!isBillingCodeColumn(fieldRaw)) {
		return null;
	}

	return { index, field: fieldRaw };
}

function asPath<T>(p: string): Path<T> {
	return p as Path<T>;
}

function billingCodesSignature(rows: BillingCodeFM[]): string {
	return JSON.stringify(
		(rows ?? []).map((r) => ([
			r.id ?? '',
			r.databaseId ?? '',
			r.deletedAt ?? '',
			r.editMode ?? '',
			r.lineItemNumber ?? '',
			r.customerId ?? '',
			r.unit ?? '',
			r.unitPrice ?? '',
			r.groupId ?? '',
			r.groupKey ?? '',
			r.description ?? '',
		]))
	);
}

function isExistingBillingCode(row: BillingCodeRHFRow): boolean {
	return !!row.databaseId;
}

function isBillingCodeEditable(row: BillingCodeRHFRow): boolean {
	if (!!row.deletedAt) {
		return false;
	}

	if (!isExistingBillingCode(row)) {
		return true;
	}

	return !!row.editMode;
}

function shouldPromptForBillingCodeEditMode(row: BillingCodeRHFRow): boolean {
	if (row.deletedAt) {
		return false;
	}

	if (!isExistingBillingCode(row)) {
		return false;
	}

	return !row.editMode;
}

const renderEditModeTooltip = (editMode: BillingCodeEditMode) => {
	if (editMode === BillingCodeEditMode.DIRECT_EDIT) {
		return (
			<div>
				<strong>Edit & recalculate all history</strong>
				<div>Fixes a wrong price or typo. The corrected value is applied retroactively.</div>
			</div>
		);
	}

	if (editMode === BillingCodeEditMode.ARCHIVE_AND_EDIT) {
		return (
			<div>
				<strong>Archive and create new</strong>
				<div>Current code is archived. New version takes effect from the date you choose. Best for yearly contract renewals.</div>
			</div>
		);
	}

	return null;
};

interface FormValues {
	billingCodes: BillingCodeRHFRow[];
}

export interface OwnProps {
	initialBillingCodes: BillingCodeFM[];
	importedBillingCodes: NormalizedImport[];
	lineItemNumberCounter: Record<number, number>;
	customerIdCounter: Record<string, number>;
	openBulkImportModal: () => Promise<void>;
	onChange?: (billingCodes: BillingCodeFM[]) => void;
	billingCodeGroups: BillingCodeGroupFM[];
}

export const fieldNameMapper: Record<BillingCodeColumn, string> = {
	rhfId: 'RHF ID',
	id: 'ID',
	databaseId: 'Database ID',
	deletedAt: 'Deleted At',
	createdAt: 'Created At',
	editMode: 'Edit Mode',
	lineItemNumber: 'Line Item Number',
	customerNumber: 'Customer Number',
	customerId: 'Customer ID',
	ownerNumber: 'Owner Number',
	ownerId: 'Owner ID',
	unit: 'Unit',
	unitPrice: 'Unit Price',
	bidQuantity: 'Bid Quantity',
	groupId: 'Group ID',
	groupKey: 'Group Key',
	group: 'Group',
	groupColor: 'Group Color',
	description: 'Description',
};

type Props = OwnProps;

const BillingCodeArray: React.FC<Props> = React.memo((props) => {
	const {
		importedBillingCodes,
		lineItemNumberCounter,
		customerIdCounter,
		openBulkImportModal,
		onChange,
		initialBillingCodes,
		billingCodeGroups,
	} = props;

	const [rowPendingEditMode, setRowPendingEditMode] = React.useState<Nullable<number>>(null);
	const [rowPendingArchive, setRowPendingArchive] = React.useState<Nullable<number>>(null);

	const openEditModeModal = React.useCallback((rowIndex: number) => () => {
		setRowPendingEditMode(rowIndex);
	}, []);

	const closeEditModeModal = React.useCallback(() => {
		setRowPendingEditMode(null);
	}, []);

	const promptEditModeIfNeeded = React.useCallback((row: BillingCodeRHFRow & SimpleTableRow) => {
		if (!shouldPromptForBillingCodeEditMode(row)) {
			return false;
		}

		setRowPendingEditMode(row.index);
		return true;
	}, []);

	const tableRef = React.useRef<TableRef>(null);

	const originalBillingCodesByDatabaseId = React.useMemo(() => {
		return (initialBillingCodes ?? []).reduce<Record<number, BillingCodeFM>>((acc, billingCode) => {
			if (billingCode.databaseId) {
				acc[billingCode.databaseId] = billingCode;
			}

			return acc;
		}, {});
	}, [initialBillingCodes]);

	const makeInitialRows = React.useCallback((rows: BillingCodeFM[]): BillingCodeRHFRow[] => {
		return (rows ?? []).map((r) => ({
			...r,
			rhfId: nanoid(UNIQUE_ID_SIZE),
		}));
	}, []);

	const {
		control,
		getValues,
		unregister,
		setError,
		clearErrors,
		reset,
		setValue,
	} = useForm<FormValues>({
		defaultValues: {
			billingCodes: makeInitialRows(initialBillingCodes ?? []),
		},
		mode: 'onChange',
	});

	const didInitRef = React.useRef(false);

	React.useEffect(() => {
		// Only initialize from props once
		if (didInitRef.current || !initialBillingCodes.length) {
			return;
		}
		didInitRef.current = true;

		reset({ billingCodes: makeInitialRows(initialBillingCodes ?? []) });
	}, [initialBillingCodes, makeInitialRows, reset]);

	const emitToParent = React.useCallback(() => {
		if (!onChange) {
			return;
		}
		const next = (getValues('billingCodes') ?? []).map(({ ...args }) => ({ ...args, rhfId: undefined }));
		onChange(next);
	}, [getValues, onChange]);

	const billingCodesWatch = useWatch({ control, name: 'billingCodes' });

	const lastEmittedSigRef = React.useRef<string>('');

	React.useEffect(() => {
		if (!onChange) {
			return;
		}

		const next = billingCodesWatch ?? [];
		const sig = billingCodesSignature(next);

		if (sig === lastEmittedSigRef.current) {
			return;
		}

		lastEmittedSigRef.current = sig;
		onChange(next);
	}, [billingCodesWatch, onChange]);

	const {
		fields,
		append,
		remove: removeBillingCodeRow,
	} = useFieldArray<FormValues, 'billingCodes', 'rhfId'>({
		control,
		name: 'billingCodes',
		keyName: 'rhfId',
	});

	const archiveBillingCodeRow = React.useCallback((index?: number | number[]) => {
		if (index === undefined || Array.isArray(index)) {
			removeBillingCodeRow(index);
			return;
		}

		const row = getValues(`billingCodes.${index}`);

		if (!row) {
			return;
		}

		// New unsaved row: safe to remove.
		if (!row.databaseId) {
			removeBillingCodeRow(index);
			return;
		}

		const originalBillingCode = originalBillingCodesByDatabaseId[row.databaseId];

		if (!originalBillingCode) {
			return;
		}

		setValue(`billingCodes.${index}`, {
			...originalBillingCode,
			// Preserve RHF identity, otherwise row remounts weirdly.
			rhfId: row.rhfId,
			deletedAt: new Date(),
			editMode: null,
		}, {
			shouldDirty: true,
			shouldTouch: true,
		});

		clearErrors(`billingCodes.${index}` as Path<FormValues>);

		emitToParent();
	}, [
		clearErrors,
		emitToParent,
		getValues,
		originalBillingCodesByDatabaseId,
		removeBillingCodeRow,
		setValue,
	]);

	const closeArchiveModal = React.useCallback(() => {
		setRowPendingArchive(null);
	}, []);

	const confirmArchiveBillingCode = React.useCallback(() => {
		if (rowPendingArchive === null) {
			return;
		}

		archiveBillingCodeRow(rowPendingArchive);
		closeArchiveModal();
	}, [archiveBillingCodeRow, closeArchiveModal, rowPendingArchive]);

	const promptArchiveBillingCodeRow = React.useCallback((index?: number | number[]) => {
		if (index === undefined || Array.isArray(index)) {
			archiveBillingCodeRow(index);
			return;
		}

		const row = getValues(`billingCodes.${index}`);

		if (!row) {
			return;
		}

		// Unsaved row: delete immediately.
		if (!row.databaseId) {
			archiveBillingCodeRow(index);
			return;
		}

		setRowPendingArchive(index);
	}, [archiveBillingCodeRow, getValues]);

	const selectedBillingCodeForArchive = React.useMemo(() => {
		if (rowPendingArchive === null) {
			return null;
		}

		const row = getValues(`billingCodes.${rowPendingArchive}`);

		if (!row) {
			return null;
		}

		if (row.databaseId) {
			return originalBillingCodesByDatabaseId[row.databaseId] ?? row;
		}

		return row;
	}, [getValues, originalBillingCodesByDatabaseId, rowPendingArchive]);

	const filterDropdown = React.useCallback((option: DropdownOption<QuantityUnitType> | GroupDropdownOption, searchText: string) => {
		return option.label.toLowerCase().includes(searchText);
	}, []);

	const validateBillingCodesArray = React.useCallback(() => {
		const values = getValues();

		clearErrors();

		validateFieldArray({
			...values,
			billingCodes: values.billingCodes.filter((billingCode) => !billingCode.deletedAt),
		}, setError, {
			requiredFields,
			numberFields,
			uniqueFields,
			fieldNameMapper,
			arrayKey: 'billingCodes',
		});
	}, [getValues, clearErrors, setError]);

	React.useEffect(() => {
		if (!importedBillingCodes?.length) {
			return;
		}

		for (const billingCode of importedBillingCodes) {

			append({
				rhfId: nanoid(UNIQUE_ID_SIZE),
				id: nanoid(UNIQUE_ID_SIZE),
				databaseId: null,
				deletedAt: null,
				editMode: null,
				createdAt: new Date(),
				customerId: billingCode.customerId,
				customerNumber: billingCode.customerNumber ?? null,
				ownerId: billingCode.ownerId ?? null,
				ownerNumber: billingCode.ownerNumber ?? null,
				unit: billingCode.unit,
				unitPrice: billingCode.unitPrice,
				bidQuantity: billingCode.bidQuantity ? `${billingCode.bidQuantity}` : null,
				groupId: billingCode.resolvedGroupId ?? null,
				groupKey: billingCode.resolvedGroupKey ?? null,
				groupColor: billingCode.resolvedGroupColor ?? null,
				group: billingCode.group ?? null,
				lineItemNumber: billingCode.lineItemNumber,
				description: billingCode.description,
			});
		}

		validateBillingCodesArray();
	}, [append, importedBillingCodes, validateBillingCodesArray]);

	const onDeleteBillingCode = React.useCallback(async () => {
		validateBillingCodesArray();
	}, [validateBillingCodesArray]);

	const validateOnChange = React.useCallback((name: string) => {
		const parsed = parseBillingCodePath(name);
		if (!parsed) {
			return;
		}

		const { index, field } = parsed;
		const cellPath = `billingCodes.${index}.${field}` as const;

		const value = getValues(asPath<FormValues>(cellPath));

		clearErrors(asPath<FormValues>(cellPath));

		if (uniqueFields.includes(field)) {
			validateBillingCodesArray();
			return;
		}

		validateOneField({
			cellName: cellPath,
			fieldName: field,
			value,
			setError,
			config: {
				requiredFields,
				numberFields,
				fieldNameMapper,
				arrayKey: 'billingCodes',
			},
		});
	}, [getValues, clearErrors, setError, validateBillingCodesArray]);

	// things that affect group display in row
	const groupsDisplaySig = React.useMemo(() => {
		return JSON.stringify(
			(billingCodeGroups ?? []).map((g) => ({
				id: g.id,
				name: (g.name ?? '').trim(),
				color: g.color ?? '',
				databaseId: g.databaseId ?? '',
			}))
		);
	}, [billingCodeGroups]);

	const groupByKey = React.useMemo(() => {
		const map = new Map<string, { name: string; databaseId: Nullable<number>; }>();

		for (const group of billingCodeGroups ?? []) {
			const key = `${group.id}`;
			const name = (group.name ?? '').trim();

			if (name) {
				map.set(key, { name, databaseId: group.databaseId ?? null });
			}
		}

		return map;
		// only recompute memoized value when groups display signature changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [groupsDisplaySig]);

	const onBillingCodeCellChange = React.useCallback(
		(name: string) => {
			const parsed = parseBillingCodePath(name);
			if (!parsed) {
				return;
			}

			const { index, field } = parsed;
			const cellPath = `billingCodes.${index}.${field}` as const;

			const value = getValues(asPath<FormValues>(cellPath));

			if (field === 'groupKey') {
				const key = value === null || `${value}`.trim() === '' ? null : `${value}`;

				if (!key) {
					setValue(asPath<FormValues>(`billingCodes.${index}.groupKey`), null, { shouldDirty: true, shouldTouch: true });
					setValue(asPath<FormValues>(`billingCodes.${index}.groupId`), null, { shouldDirty: true, shouldTouch: true });
					setValue(asPath<FormValues>(`billingCodes.${index}.group`), null, { shouldDirty: true, shouldTouch: true });
				} else {
					const billingCodeGroup = groupByKey.get(key);

					if (!billingCodeGroup) {
						setValue(asPath<FormValues>(`billingCodes.${index}.groupKey`), null, { shouldDirty: true, shouldTouch: true });
						setValue(asPath<FormValues>(`billingCodes.${index}.groupId`), null, { shouldDirty: true, shouldTouch: true });
						setValue(asPath<FormValues>(`billingCodes.${index}.group`), null, { shouldDirty: true, shouldTouch: true });
					} else {
						setValue(asPath<FormValues>(`billingCodes.${index}.groupId`), billingCodeGroup.databaseId ?? null, { shouldDirty: true, shouldTouch: true });
						setValue(asPath<FormValues>(`billingCodes.${index}.group`), billingCodeGroup.name, { shouldDirty: true, shouldTouch: true });
					}
				}
			}

			validateOnChange(name);
			emitToParent();
		},
		[emitToParent, getValues, groupByKey, setValue, validateOnChange]
	);

	const canDeleteRow = React.useCallback((row: BillingCodeRHFRow & SimpleTableRow) => {
		return !row.deletedAt;
	}, []);

	const groupOptions: GroupDropdownOption[] = React.useMemo(() => {
		return filterMap(
			billingCodeGroups ?? [],
			(g) => g.name?.trim() !== '' && !g.deletedAt,
			(g) => ({
				id: `${g.id}`,
				label: g.name?.trim(),
				color: g.color,
			}))
			.sort((a, b) => a.label?.localeCompare(b.label, undefined, { sensitivity: 'base' }));
	}, [billingCodeGroups]);

	const renderGroupOption = React.useCallback(
		(option: Nullable<GroupDropdownOption>) => {
			if (!option) {
				return <div />;
			}

			return <div className={styles['group-option']}>{option.color && <ColorSquare color={option.color} />}{option.label}</div>;
		}, []
	);

	const renderReadonlyValue = React.useCallback((
		value: string | number,
		cellType: 'text' | 'numeric' | 'dollar' | 'quantity',
		isDeleted: boolean,
		onClick?: () => void
	) => {
		return (
			<div
				className={`${styles[`readonly-cell--${cellType}`]} ${isDeleted && styles['readonly-cell--archived']} ${onClick ? styles['readonly-cell--clickable'] : ''}`}
				onClick={onClick}
			>
				<div>{cellType === 'dollar' && '$'}</div>
				<span>{value ?? ''}</span>
			</div>
		);
	}, []);

	const renderReadonlyGroup = React.useCallback((row: BillingCodeRHFRow & SimpleTableRow, onClick?: () => void) => {
		const className = `${styles['readonly-cell']} ${!!row.deletedAt ? styles['readonly-cell--archived'] : ''} ${onClick ? styles['readonly-cell--clickable'] : ''}`;

		return (
			<span className={className} onClick={onClick}>
				{row.groupColor && <ColorSquare color={row.groupColor} />}
				{row.group ?? ''}
			</span>
		);
	}, []);

	const renderEditModeCell = React.useCallback(
		(cell: CellContext<BillingCodeRHFRow & SimpleTableRow, unknown>) => {
			const row = cell.row.original;

			if (row.deletedAt || !row.databaseId || !row.editMode) {
				return <div className={`${row.deletedAt ? `${styles['readonly-cell--archived']}` : `${styles['readonly-cell']}`}`} />;
			}

			return (
				<Tooltip message={renderEditModeTooltip(row.editMode)}>
					<span
						className={styles['info-icon']}
						onClick={openEditModeModal(row.index)}
					>
						<span className="icon-info" />
					</span>
				</Tooltip>
			);
		},
		[openEditModeModal]
	);

	const selectedBillingCodeForEditMode = React.useMemo(() => {
		if (rowPendingEditMode === null) {
			return null;
		}

		return getValues(`billingCodes.${rowPendingEditMode}` as const) ?? null;
	}, [getValues, rowPendingEditMode]);

	const submitEditMode = React.useCallback((mode: BillingCodeEditMode) => {
		if (rowPendingEditMode === null) {
			return;
		}

		const row = getValues(`billingCodes.${rowPendingEditMode}`);

		if (!row) {
			closeEditModeModal();
			return;
		}

		if (!row.databaseId) {
			closeEditModeModal();
			return;
		}

		const originalBillingCode = originalBillingCodesByDatabaseId[row.databaseId];

		if (mode === BillingCodeEditMode.DIRECT_EDIT && originalBillingCode) {
			setValue(`billingCodes.${rowPendingEditMode}.unit`, originalBillingCode.unit, {
				shouldDirty: true,
				shouldTouch: true,
			});

			clearErrors(`billingCodes.${rowPendingEditMode}.unit` as Path<FormValues>);
		}

		setValue(`billingCodes.${rowPendingEditMode}.editMode`, mode, {
			shouldDirty: true,
			shouldTouch: true,
		});

		emitToParent();
		closeEditModeModal();
	}, [
		clearErrors,
		closeEditModeModal,
		emitToParent,
		getValues,
		originalBillingCodesByDatabaseId,
		rowPendingEditMode,
		setValue,
	]);

	const onClearGroup = React.useCallback((rowIndex: number) => () => {
		setValue(`billingCodes.${rowIndex}.groupKey`, null, { shouldDirty: true, shouldTouch: true });
		setValue(`billingCodes.${rowIndex}.groupId`, null, { shouldDirty: true, shouldTouch: true });
		setValue(`billingCodes.${rowIndex}.group`, null, { shouldDirty: true, shouldTouch: true });

		validateOnChange(`billingCodes.${rowIndex}.groupKey`);

		emitToParent();
	}, [emitToParent, setValue, validateOnChange]);

	const renderGroupDropdownCell = React.useCallback(
		() => (cell: CellContext<BillingCodeRHFRow & SimpleTableRow, unknown>) => {
			const original = cell.row.original;

			if (!isBillingCodeEditable(original)) {
				const onClick = shouldPromptForBillingCodeEditMode(original)
					? () => promptEditModeIfNeeded(original)
					: undefined;

				return renderReadonlyGroup(original, onClick);
			}

			return (
				<DropdownCellRenderer
					control={control}
					fieldsLength={fields.length}
					filterable={true}
					filterBy={filterDropdown}
					getValues={getValues}
					id={`${original.rhfId}`}
					labelKey="label"
					name={`billingCodes.${original.index}.groupKey`}
					onChange={onBillingCodeCellChange}
					onClear={onClearGroup(original.index)}
					options={groupOptions}
					placeholder="Select Group"
					renderMenuItem={renderGroupOption}
					rhfId={original.rhfId}
					unregister={unregister}
					valueKey="id"
					withCaret={true}
				/>
			);
		},
		[
			control,
			fields.length,
			filterDropdown,
			getValues,
			onBillingCodeCellChange,
			onClearGroup,
			groupOptions,
			renderGroupOption,
			unregister,
			renderReadonlyGroup,
			promptEditModeIfNeeded,
		]
	);

	const renderUnitDropdownCell = React.useCallback(
		(cellName: keyof BillingCodeRHFRow) => (cell: CellContext<BillingCodeRHFRow & SimpleTableRow, string>) => {
			const original = cell.row.original;
			const isDirectEditMode = original.editMode === BillingCodeEditMode.DIRECT_EDIT;

			if (!isBillingCodeEditable(original) || isDirectEditMode) {
				const onClick = !isDirectEditMode && shouldPromptForBillingCodeEditMode(original)
					? () => promptEditModeIfNeeded(original)
					: undefined;

				const value = cell.getValue();
				const label = value ? QuantityUnitLabel[value as QuantityUnitType] ?? value : '';

				return renderReadonlyValue(label, 'text', !!original.deletedAt, onClick);
			}

			return (
				<DropdownCellRenderer
					control={control}
					fieldsLength={fields.length}
					filterable={true}
					filterBy={filterDropdown}
					getValues={getValues}
					id={`${original.rhfId}`}
					labelKey="label"
					name={`billingCodes.${original.index}.${cellName}`}
					onChange={onBillingCodeCellChange}
					options={quantityUnitOptions}
					placeholder="Select Unit"
					rhfId={original.rhfId}
					unregister={unregister}
					valueKey="id"
					withCaret={true}
				/>
			);
		},
		[control, fields.length, filterDropdown, getValues, onBillingCodeCellChange, promptEditModeIfNeeded, renderReadonlyValue, unregister]
	);

	const renderInputCell = React.useCallback(
		(cellName: keyof BillingCodeRHFRow, cellType: 'text' | 'numeric' | 'dollar' | 'quantity') =>
			(cell: CellContext<BillingCodeRHFRow & SimpleTableRow, string>) => {
				const original = cell.row.original;

				if (!isBillingCodeEditable(original)) {
					const onClick = shouldPromptForBillingCodeEditMode(original)
						? () => promptEditModeIfNeeded(original)
						: undefined;

					return renderReadonlyValue(cell.getValue(), cellType, !!original.deletedAt, onClick);
				}

				return (
					<InputCellRenderer
						control={control}
						defaultValue={(cell.getValue()) ?? ''}
						fieldsLength={fields.length}
						getValues={getValues}
						name={`billingCodes.${original.index}.${cellName}`}
						onChange={onBillingCodeCellChange}
						rhfId={original.rhfId}
						type={cellType}
						unregister={unregister}
					/>
				);
			},
		[control, fields.length, getValues, onBillingCodeCellChange, promptEditModeIfNeeded, renderReadonlyValue, unregister]
	);

	const renderTextAreaCell = React.useCallback(
		(cellName: keyof BillingCodeRHFRow) => (cell: CellContext<BillingCodeRHFRow & SimpleTableRow, string>) => {
			const original = cell.row.original;

			if (!isBillingCodeEditable(original)) {
				const onClick = shouldPromptForBillingCodeEditMode(original)
					? () => promptEditModeIfNeeded(original)
					: undefined;

				return renderReadonlyValue(cell.getValue(), 'text', !!original.deletedAt, onClick);
			}

			return (
				<TextAreaCellRenderer
					control={control}
					defaultValue={(cell.getValue()) ?? ''}
					fieldsLength={fields.length}
					getValues={getValues}
					name={`billingCodes.${original.index}.${cellName}`}
					onChange={onBillingCodeCellChange}
					rhfId={original.rhfId}
					unregister={unregister}
				/>
			);
		},
		[control, fields.length, getValues, onBillingCodeCellChange, promptEditModeIfNeeded, renderReadonlyValue, unregister]
	);

	const columns: SimpleTableProps<BillingCodeRHFRow & SimpleTableRow>['columns'] = React.useMemo(
		() => [
			{
				id: 'lineItemNumber',
				accessor: 'lineItemNumber',
				header: 'Line Item No. *',
				enableSorting: true,
				cell: renderInputCell('lineItemNumber', 'numeric'),
			},
			{
				id: 'customerNumber',
				accessor: 'customerNumber',
				header: 'Customer No.',
				enableSorting: true,
				cell: renderInputCell('customerNumber', 'numeric'),
			},
			{
				id: 'customerId',
				accessor: 'customerId',
				header: 'Customer ID *',
				enableSorting: true,
				cell: renderInputCell('customerId', 'text'),
			},
			{
				id: 'ownerNumber',
				accessor: 'ownerNumber',
				header: 'Owner Number',
				enableSorting: true,
				cell: renderInputCell('ownerNumber', 'numeric'),
			},
			{
				id: 'ownerId',
				accessor: 'ownerId',
				header: 'Owner ID',
				enableSorting: true,
				cell: renderInputCell('ownerId', 'text'),
			},
			{
				id: 'bidQuantity',
				accessor: 'bidQuantity',
				header: 'Bid Qty',
				enableSorting: true,
				cell: renderInputCell('bidQuantity', 'quantity'),
			},
			{
				id: 'unit',
				accessor: 'unit',
				header: 'Unit *',
				enableSorting: true,
				cell: renderUnitDropdownCell('unit'),
				size: '120px',
			},
			{
				id: 'unitPrice',
				accessor: 'unitPrice',
				header: 'Unit Price *',
				enableSorting: true,
				cell: renderInputCell('unitPrice', 'dollar'),
			},
			{
				id: 'groupKey',
				accessor: 'groupKey',
				header: 'Group',
				enableSorting: true,
				cell: renderGroupDropdownCell(),
			},
			{
				id: 'description',
				accessor: 'description',
				header: 'Description *',
				enableSorting: true,
				cell: renderTextAreaCell('description'),
				size: '2fr',
			},
			{
				id: 'edit',
				isDisplayColumn: true,
				header: null,
				cell: renderEditModeCell,
				size: 32,
			},
		],
		[renderEditModeCell, renderGroupDropdownCell, renderInputCell, renderTextAreaCell, renderUnitDropdownCell]
	);

	const addNewBillingCode = React.useCallback(async () => {
		append({
			rhfId: nanoid(UNIQUE_ID_SIZE),
			id: nanoid(UNIQUE_ID_SIZE),
			databaseId: null,
			deletedAt: null,
			createdAt: new Date(),
			editMode: null,
			customerId: '',
			customerNumber: null,
			ownerId: null,
			ownerNumber: null,
			unit: null,
			unitPrice: '',
			bidQuantity: null,
			groupId: null,
			groupKey: null,
			group: null,
			groupColor: null,
			lineItemNumber: null,
			description: '',
		});

		requestAnimationFrame(() => {
			tableRef.current?.scrollToBottomRow();
		});
	}, [append]);

	React.useEffect(() => {
		const rows = getValues('billingCodes') ?? [];

		for (let index = 0; index < rows.length; index++) {
			const key = rows[index]?.groupKey;
			if (!key) {
				// clear display value if no selection
				if (rows[index]?.group !== null) {
					setValue(`billingCodes.${index}.group`, null, { shouldDirty: true, shouldTouch: true });
				}
				continue;
			}

			const name = groupByKey.get(key)?.name ?? null;

			// If selected key no longer exists, clear
			if (!name) {
				setValue(`billingCodes.${index}.groupKey`, null, { shouldDirty: true, shouldTouch: true });
				setValue(`billingCodes.${index}.groupId`, null, { shouldDirty: true, shouldTouch: true });
				setValue(`billingCodes.${index}.group`, null, { shouldDirty: true, shouldTouch: true });
				continue;
			}

			// Keep group display name in sync with current group name
			if ((rows[index]?.group ?? null) !== name) {
				setValue(`billingCodes.${index}.group`, name, { shouldDirty: true, shouldTouch: true });
			}
		}
	}, [groupByKey, getValues, setValue]);

	const footerButtonsLeft = React.useMemo<FooterButton[]>(
		() => [
			{
				iconName: 'ActionPlusSmall',
				label: 'Add Billing Code',
				onClick: addNewBillingCode,
			},
		],
		[addNewBillingCode]
	);

	const headerButtons = React.useMemo<FooterButton[]>(() => [{
		iconName: 'ActionImport',
		label: 'CSV Import',
		onClick: openBulkImportModal,
	}], [openBulkImportModal]);

	const rowClassName = React.useCallback((_row: Row<BillingCodeRHFRow & SimpleTableRow>) => {
		const classNames: string[] = [];

		if (_row.original.deletedAt) {
			classNames.push(styles['archived-row']);
		}

		if ((_row.original.lineItemNumber && lineItemNumberCounter[_row.original.lineItemNumber] > 1)
			|| customerIdCounter[_row.original.customerId] > 1
		) {
			classNames.push(styles['error-row']);
		}

		return classNames.join(' ');
	}, [customerIdCounter, lineItemNumberCounter]);

	const fieldsForTable = React.useMemo(() => {
		return fields.map((field, index) => {
			const watchedRow = billingCodesWatch?.[index] ?? field;
			const rhfId = field.rhfId;

			return {
				...field,
				...watchedRow,
				id: rhfId,
				rhfId,
			};
		});
	}, [billingCodesWatch, fields]);

	return (
		<>
			<SimpleTableRHFWrapper<
				BillingCodeRHFRow & SimpleTableRow,
				FormValues,
				'billingCodes'
			>
				canDeleteRow={canDeleteRow}
				columns={columns}
				emptyTableMessage="No Billing Codes found."
				fields={fieldsForTable}
				footerButtonsLeft={footerButtonsLeft}
				formAccessorName="billingCodes"
				headerButtons={headerButtons}
				height="695px"
				iconName="Archive"
				label="Billing Codes"
				// labelTooltip="Charge rates assigned to billable work items for this job. Billing codes are required fields for billable work
				// on both the Field Report Work Summary and the Jobs Work Summary."
				onRemove={onDeleteBillingCode}
				ref={tableRef}
				remove={promptArchiveBillingCodeRow}
				rowClassName={rowClassName}
			/>
			<BillingCodeEditModeModal
				billingCode={selectedBillingCodeForEditMode}
				onClose={closeEditModeModal}
				onSubmit={submitEditMode}
				showModal={rowPendingEditMode !== null}
			/>
			<BillingCodeArchiveModal
				billingCode={selectedBillingCodeForArchive}
				onClose={closeArchiveModal}
				onConfirm={confirmArchiveBillingCode}
				showModal={rowPendingArchive !== null}
			/>
		</>

	);
});

export default React.memo(BillingCodeArray);
